// src/features/records/services/edges/subscribe.js

import { db } from '../../../../firebase';
import {
  collection,
  documentId,
  limit,
  onSnapshot,
  orderBy,
  query,
  where
} from 'firebase/firestore';
import { getQueryFieldKey, getViewFlagKey } from './keys';

/**
 * Subscribe to edges for a specific view with an optional bounded live window.
 */
export function subscribeEdges(
  { uid, viewType, scope, sortSpec, pageSize = 30, cursor: _cursor = null, range = null },
  onDelta,
  onSnapshotOrdered
) {
  void _cursor;
  const flagKey = getViewFlagKey(viewType);
  let sortQueryKey = getQueryFieldKey(sortSpec.key);

  const base = collection(db, `users/${uid}/accessibleRecords`);
  const clauses = [where('lifecycle', '==', 'active')];

  if (viewType !== 'note') {
    clauses.push(where(`viewFlags.${flagKey}`, '==', true));
  }

  if (scope?.mode === 'users' && scope.userIds?.length) {
    clauses.push(where('ownerId', 'in', scope.userIds.slice(0, 10)));
  } else if (scope?.mode === 'group' && scope.groupId) {
    clauses.push(where('permission.sourcesGroupIds', 'array-contains', scope.groupId));
  }

  if (range?.key && (range.start || range.end)) {
    const rangeQueryKey = getQueryFieldKey(range.key);

    if (rangeQueryKey !== sortQueryKey) {
      console.warn(
        `subscribeEdges range key (${range.key}) != sort key (${sortSpec.key}). ` +
          'Firestore requires ordering by the inequality field.'
      );
      sortQueryKey = rangeQueryKey;
    }

    if (range.start) clauses.push(where(`query.${rangeQueryKey}`, '>=', range.start));
    if (range.end) clauses.push(where(`query.${rangeQueryKey}`, '<', range.end));
  }

  clauses.push(
    orderBy(`query.${sortQueryKey}`, sortSpec.dir),
    orderBy(documentId(), sortSpec.dir)
  );

  const shouldLimit = typeof pageSize === 'number' && Number.isFinite(pageSize) && pageSize > 0;
  if (shouldLimit) {
    clauses.push(limit(Math.max(1, Math.floor(pageSize))));
  }

  const q = query(base, ...clauses);

  const unsub = onSnapshot(q, (snap) => {
    if (onSnapshotOrdered) {
      onSnapshotOrdered(snap);
    }
    snap.docChanges().forEach((ch) => {
      onDelta({
        type: ch.type,
        edge: ch.doc.data(),
        cursor: ch.doc
      });
    });
  });

  return unsub;
}
